/**
 * How a component **above** `App` reaches the navigation api.
 *
 * There is exactly one such component and there is meant to stay exactly one: `LightboxProvider`,
 * which mounts above `App` on purpose — its capture-phase keydown listener has to beat every
 * surface's, and it has to survive the unmount a rail click does. That puts it out of reach of any
 * context `App` could provide, so the binding goes the other way round.
 *
 * `App` binds on mount and unbinds on unmount; React flushes a child's effects before its parent's,
 * so the api is always bound by the time the provider's own effect registers its closer. Everything
 * else in the app is inside `App` and should take the api as a prop — **do not reach for this to
 * avoid passing one down.** A second caller here is a sign something has been mounted in the wrong
 * place.
 *
 * Every accessor answers `null` before binding rather than throwing: a lightbox that opens without
 * a history entry is a lightbox Back cannot close, which is exactly how it behaved before 日本-321
 * and is survivable. Taking the app down over it is not.
 */

import type { NavApi } from "./useHistoryNav";

/** The overlay half of the api — the only part anything above `App` has any business with. */
export type OverlayApi = Pick<NavApi, "openOverlay" | "closeOverlay" | "registerOverlay">;

let bound: OverlayApi | null = null;

export function bindOverlayApi(api: OverlayApi | null): void {
  bound = api;
}

export function overlayApi(): OverlayApi | null {
  return bound;
}
